import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Text } from '@fluentui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { EditItem, useFetch } from '@moko/ui';
import AppContext from '../AppContext';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  createPricingRate,
  getPricingItems,
  getPricingRate,
  IPricingRateBody,
  updatePricingRate,
} from '../../services/pricingService';
import { getCurrencies, ICatalogCurrency } from '../../services/tenantService';
import { pricingRatesPath, toDateOnly } from './pricingSettings';

const text = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const toBody = (
  item: Record<string, unknown>
): { body: IPricingRateBody } | { error: string } => {
  const pricingItemId = Number(item.pricingItemId);
  const currencyCode = text(item.amount_Unit);
  const amount = Number(item.amount);
  const effectiveFrom = text(item.effectiveFrom);
  const effectiveTo = text(item.effectiveTo);
  if (
    !Number.isFinite(pricingItemId) ||
    pricingItemId <= 0 ||
    !currencyCode ||
    !Number.isFinite(amount) ||
    !effectiveFrom
  ) {
    return {
      error: 'Pricing item, currency, amount, and effective from are required.',
    };
  }
  return {
    body: {
      pricingItemId,
      currencyCode,
      amount,
      effectiveFrom: toDateOnly(effectiveFrom),
      effectiveTo: effectiveTo ? toDateOnly(effectiveTo) : null,
    },
  };
};

const currencyLabel = (code: string) => {
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'currency' }).of(code);
    return name ? `${name} (${code})` : code;
  } catch {
    return code;
  }
};

const PricingRateForm = () => {
  const isSaved = useRef(false);
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const pricingPath = pricingRatesPath;
  const rateId = Number.parseInt(query.get('id') ?? '');
  const isEdit = !Number.isNaN(rateId) && rateId > 0;
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const context = useContext(AppContext);
  const title = isEdit ? 'Edit pricing rate' : 'Add pricing rate';

  useEffect(() => {
    context.setSelectedTab('Global settings');
  }, []);
  const [itemOptions, setItemOptions] = useState<
    { key: string; value: string }[]
  >([]);
  const currencyFetch = useFetch<ICatalogCurrency[]>({
    queryKey: ['currencies'],
    queryFn: getCurrencies,
  });
  const currencyOptions = useMemo(
    () =>
      (currencyFetch.data ?? []).map((currency) => ({
        key: currency.code,
        value: currency.code,
        optionLabel: currencyLabel(currency.code),
      })),
    [currencyFetch.data]
  );

  useEffect(() => {
    const abortController = new AbortController();
    getPricingItems(abortController, 500, 1)
      .then((page) => {
        setItemOptions(
          page.items.map((item) => ({
            key: String(item.id),
            value: item.name || item.code,
          }))
        );
      })
      .catch((error: { message?: string }) => {
        if (abortController.signal.aborted) return;
        setErrorMessage(error.message ?? 'Failed to load pricing items.');
      });
    return () => abortController.abort();
  }, [setErrorMessage]);

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <Text variant="xLarge">{title}</Text>
      <EditItem
        getAction={async (abortController, id) => {
          const rate = await getPricingRate(abortController, id);
          return { ...rate, amount_Unit: rate.currencyCode };
        }}
        newAction={async (abortController, item) => {
          const result = toBody(item);
          if ('error' in result) return;
          await createPricingRate(abortController, result.body);
          setSuccessMessage('Pricing rate created.');
          navigate(pricingPath);
        }}
        editAction={async (abortController, id, item) => {
          const result = toBody(item);
          if ('error' in result) return;
          await updatePricingRate(abortController, id, result.body);
          setSuccessMessage(`Pricing rate ${id} updated.`);
          navigate(pricingPath);
        }}
        isSaved={isSaved}
        back={pricingPath}
        confirmOnCancel={false}
        onCancel={() => navigate(pricingPath)}
        metadata={{
          fields: [
            {
              name: 'pricingItemId',
              fieldType: 'Lookup',
              label: 'Pricing item',
              lookupList: 'PricingItems',
              mandatory: true,
            },
            {
              name: 'amount',
              fieldType: 'NumberWithUnit',
              label: 'Amount',
              lookupList: 'Currencies',
              mandatory: true,
            },
            {
              name: 'effectiveFrom',
              fieldType: 'Date',
              label: 'Effective from',
              mandatory: true,
            },
            {
              name: 'effectiveTo',
              fieldType: 'Date',
              label: 'Effective to',
            },
          ],
          lookups: [
            { name: 'PricingItems', values: itemOptions },
            { name: 'Currencies', values: currencyOptions },
          ],
          validations: [
            {
              field: 'pricingItemId',
              rules: [
                { rule: 'NE', errorMessage: 'Pricing item is required.' },
              ],
            },
            {
              field: 'amount',
              rules: [
                { rule: 'NE', errorMessage: 'Amount is required.' },
                {
                  rule: 'RGX^(?:[1-9]\\d*(?:\\.\\d+)?|0+\\.\\d*[1-9]\\d*)$',
                  errorMessage: 'Amount must be greater than 0.',
                },
              ],
            },
            {
              field: 'amount_Unit',
              rules: [{ rule: 'NE', errorMessage: 'Currency is required.' }],
            },
            {
              field: 'effectiveFrom',
              rules: [
                { rule: 'NE', errorMessage: 'Effective from is required.' },
              ],
            },
          ],
          interactions: [],
        }}
      />
    </div>
  );
};

export default PricingRateForm;
